import { Link, Tooltip } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCallbackDetails,
  GridCellParams,
  GridColumnHeaderParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridHeaderCheckbox,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';

import { EXPANDED_TABLE_PROPERTIES } from '@/constants';
import { Supporter } from '@/types';
import {
  AgDataGrid,
  AgDataGridProps,
  PaginationModel,
} from '@australiangreens/ag-internal-components';
import { CollapsibleText } from '@components/CollapsibleText';
import { phoneNoObscurer } from '@components/DetailedContactsTable';
import { rocketColumn } from '@components/DetailedContactsTable/columnDefintions';
import {
  contactIdFilterOperators,
  notesFilterOperators,
  redisBetterOperators,
  redisNameFilterOperators,
  searchResultsPhoneFilterOperators,
} from '@components/DetailedContactsTable/columnOperators';
import { useMemo } from 'react';

const filteredSupportersListMessages = 'There are no supporters to display shown by the filter.';

type ContactsTableProps = {
  data: Supporter[];
  isLoading: boolean;
  paginationModel: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  sortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails
  ) => void;
  onFilterModelChange?: (model: GridFilterModel) => void;
  filterModel?: GridFilterModel;
  filterMode?: 'client' | 'server';
  paginationMode?: 'client' | 'server';
  checkboxSelection?: boolean;
  rowSelectionModel?: number[];
  setSelectionModel?: (value: number[]) => void;
  idUrl?: (id: number) => string;
  rowCount?: AgDataGridProps['rowCount'];
  onSortModelChange?: AgDataGridProps['onSortModelChange'];
  isRedisStripping?: boolean;
  disableSelectAll?: boolean;
  idWithoutEmptyFilters?: boolean;
  /** Match phone filters against the full number, including digits hidden in the UI mask. */
  filterPhoneOnFullNumber?: boolean;
};

const ContactsTable = ({
  data,
  isLoading,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  sortModel,
  onSortModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onFilterModelChange,
  filterModel,
  filterMode,
  paginationMode,
  checkboxSelection,
  rowSelectionModel,
  setSelectionModel,
  idUrl,
  isRedisStripping,
  disableSelectAll = false,
  idWithoutEmptyFilters = false,
  filterPhoneOnFullNumber = false,
}: ContactsTableProps) => {
  const operatorOverload = useMemo(
    () => (isRedisStripping ? { filterOperators: redisBetterOperators } : {}),
    [isRedisStripping]
  );

  const columnDefs: AgDataGridProps['columns'] = useMemo(
    () => [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        type: 'string', // Avoid warning from MUI
        // [LIST-812] As a workaround until we migrate to datagrid v6 and can
        // have a more complete solution, we just disable the select all
        // functionality in scenarios were checkboxes would break
        renderHeader: (params: GridColumnHeaderParams) => (
          <Tooltip
            title={
              disableSelectAll ? 'Function disabled when table sorting or filtering is in use' : ''
            }
            arrow
            placement="top"
          >
            <span>
              {/* @ts-expect-error Complains that disabled is not a valid prop, but it works */}
              <GridHeaderCheckbox disabled={disableSelectAll} {...params} />
            </span>
          </Tooltip>
        ),
      },
      {
        headerName: 'Id',
        headerAlign: 'left',
        field: 'id',
        align: 'left',
        type: 'number',
        flex: 8,
        editable: false,
        ...(idWithoutEmptyFilters ? { filterOperators: contactIdFilterOperators } : {}),
        renderCell: (params: GridRenderCellParams) => {
          return idUrl ? (
            <Link href={idUrl(params.row.id)} target="_blank" title="Open Rocket in a new tab">
              {params.row.id}
            </Link>
          ) : (
            <span>{params.row.id}</span>
          );
        },
      },
      {
        headerName: 'First Name',
        field: 'preferredName',
        align: 'left',
        flex: 10,
        editable: false,
        ...(isRedisStripping
          ? { filterOperators: redisNameFilterOperators }
          : operatorOverload),
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
        align: 'left',
        flex: 10,
        editable: false,
        ...(isRedisStripping
          ? { filterOperators: redisNameFilterOperators }
          : operatorOverload),
      },
      {
        headerName: 'Phone No.',
        field: 'phone',
        align: 'left',
        flex: 10,
        editable: false,
        renderCell: (params: GridRenderCellParams) => (
          <span role="presentation">{phoneNoObscurer(params.row.phone)}</span>
        ),
        ...(filterPhoneOnFullNumber || isRedisStripping
          ? { filterOperators: searchResultsPhoneFilterOperators }
          : operatorOverload),
      },
      {
        headerName: 'Postcode',
        field: 'postalCode',
        align: 'left',
        flex: 8,
        editable: false,
        ...operatorOverload,
      },
      rocketColumn(10),
      {
        headerName: 'Notes',
        field: 'latestNotes',
        align: 'left',
        renderCell: (params: GridRenderCellParams) => (
          <CollapsibleText text={params.row.latestNotes} />
        ),
        flex: 45,
        editable: false,
        ...(isRedisStripping ? { filterOperators: notesFilterOperators } : {}),
      },
    ],
    [
      filterPhoneOnFullNumber,
      isRedisStripping,
      idWithoutEmptyFilters,
      operatorOverload,
      idUrl,
      disableSelectAll,
    ]
  );

  const isRowSelectable = disableSelectAll
    ? undefined
    : (params: GridRowParams) => {
        const { page: currentPage, pageSize } = paginationModel;
        return data
          .slice(currentPage * pageSize, (currentPage + 1) * pageSize)
          .some((supporter) => supporter.id === params.id);
      };

  return (
    <AgDataGrid
      loading={isLoading}
      rows={data}
      columns={columnDefs}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      paginationMode={paginationMode}
      sortingMode={paginationMode === 'server' ? 'server' : 'client'}
      filterMode={filterMode}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      noResultsMessage={filteredSupportersListMessages}
      skeletonColumnWidths={[10, 10, 10, 8, 8, 10, 48]}
      rowCount={rowCount}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      checkboxSelection={checkboxSelection}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      rowSelectionModel={
        rowSelectionModel != null
          ? { type: 'include' as const, ids: new Set(rowSelectionModel) }
          : undefined
      }
      onRowSelectionModelChange={
        setSelectionModel
          ? (model: GridRowSelectionModel) =>
              setSelectionModel([...model.ids].map((id) => Number(id)))
          : undefined
      }
      disableRowSelectionExcludeModel
      keepNonExistentRowsSelected
      isRowSelectable={isRowSelectable}
      getCellClassName={(params: GridCellParams) => {
        if (params.field === 'latestNotes') {
          return 'latestNotes';
        }
        return '';
      }}
      {...EXPANDED_TABLE_PROPERTIES}
    />
  );
};

export default ContactsTable;
